import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Filter, X, ArrowLeft } from "lucide-react";
import ProductCard from "./ProductCard";
import SearchBar from "./SearchBar";
import FilterSidebar from "./FilterSidebar";
import SortDropdown from "./SortDropdown";
import Pagination from "./Pagination";
import { SkeletonProductCard } from "../common/SkeletonLoader";
import EmptyState from "../common/EmptyState";
import { useProducts } from "../../hooks/useProducts";
import { apiRequest } from "../../config/api";
import FloatingFilterButton from "./FloatingFilterButton";

const SearchResultsPage = React.memo(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const searchQuery = searchParams.get("q") || "";
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const sortBy = searchParams.get("sort") || "relevance";

  // State management
  const [filters, setFilters] = useState({
    categories: searchParams.getAll("categories") || [],
    tags: searchParams.getAll("tags") || [],
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    brand: searchParams.get("brand") || "",
    minRating: searchParams.get("minRating") || "",
    inStock: searchParams.get("inStock") !== "false",
    featured: searchParams.get("featured") === "true",
  });

  const [availableFilters, setAvailableFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [mobileGridColumns, setMobileGridColumns] = useState(2); // 1 or 2 columns for mobile

  // Memoized filters object to prevent infinite re-renders
  const memoizedFilters = useMemo(
    () => ({
      page: currentPage,
      limit: 20,
      sort: sortBy,
      search: searchQuery,
      ...filters,
    }),
    [currentPage, sortBy, searchQuery, filters]
  );

  // Fetch products with search query and filters
  const { products, loading, error, pagination, refreshProducts } =
    useProducts(memoizedFilters);

  // Fetch available filters
  useEffect(() => {
    const fetchAvailableFilters = async () => {
      try {
        const data = await apiRequest("/products/available-filters");
        setAvailableFilters(data.data);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    };

    fetchAvailableFilters();
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    // Always include search query
    if (searchQuery) {
      params.set("q", searchQuery);
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach((v) => params.append(key, v));
          }
        } else if (typeof value === "boolean") {
          params.set(key, value.toString());
        } else if (value !== false) {
          params.set(key, value.toString());
        }
      }
    });

    if (currentPage > 1) {
      params.set("page", currentPage.toString());
    }

    if (sortBy !== "relevance") {
      params.set("sort", sortBy);
    }

    setSearchParams(params);
  }, [searchQuery, filters, currentPage, sortBy, setSearchParams]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };

      if (filterType === "categories" || filterType === "tags") {
        const currentValues = prev[filterType] || [];
        if (currentValues.includes(value)) {
          newFilters[filterType] = currentValues.filter((v) => v !== value);
        } else {
          newFilters[filterType] = [...currentValues, value];
        }
      } else {
        newFilters[filterType] = value;
      }

      return newFilters;
    });
  };

  // Handle search
  const handleSearch = (newSearchQuery) => {
    if (newSearchQuery !== searchQuery) {
      navigate(`/search?q=${encodeURIComponent(newSearchQuery)}`);
    }
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}&sort=${newSort}`);
  };

  // Handle page change
  const handlePageChange = (page) => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}&page=${page}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      categories: [],
      tags: [],
      minPrice: "",
      maxPrice: "",
      brand: "",
      minRating: "",
      inStock: true,
      featured: false,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "boolean") return value;
      return value !== "";
    });
  }, [filters]);

  // After fetching products, sort them so that exact name matches appear first
  const normalizedQuery = (searchQuery || "").trim().toLowerCase();
  const sortedProducts = useMemo(() => {
    if (!products) return [];
    return [...products].sort((a, b) => {
      const aExact = a.name.trim().toLowerCase() === normalizedQuery;
      const bExact = b.name.trim().toLowerCase() === normalizedQuery;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return 0;
    });
  }, [products, normalizedQuery]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">
            ⚠️ Error Loading Search Results
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshProducts}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  Search Results
                </h1>
              </div>

              {searchQuery && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Search className="h-4 w-4" />
                  <span>
                    Results for "{searchQuery}" •{" "}
                    {pagination?.totalProducts || 0} products found
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <SearchBar
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search products..."
                className="w-full sm:w-64"
                showSuggestions={true}
                onSuggestionSelect={(suggestion) => {
                  navigate(`/products/${suggestion.slug}`);
                }}
              />
              <SortDropdown
                value={sortBy}
                onChange={handleSortChange}
                className="w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-8">
              <FilterSidebar
                filters={filters}
                availableFilters={availableFilters}
                onFilterChange={handleFilterChange}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                sortBy={sortBy}
                onSortChange={handleSortChange}
              />
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Mobile: Column Toggle */}
            <div className="flex items-center justify-end mb-4 lg:hidden">
              <button
                onClick={() => setMobileGridColumns(1)}
                className={`p-2 mr-2 border rounded ${
                  mobileGridColumns === 1
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-200 text-gray-700 border-gray-300"
                }`}
                aria-label="Show 1 column"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect
                    x="4"
                    y="4"
                    width="12"
                    height="12"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <button
                onClick={() => setMobileGridColumns(2)}
                className={`p-2 border rounded ${
                  mobileGridColumns === 2
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-200 text-gray-700 border-gray-300"
                }`}
                aria-label="Show 2 columns"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="3" y="4" width="6" height="12" fill="currentColor" />
                  <rect
                    x="11"
                    y="4"
                    width="6"
                    height="12"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            {/* Remove Active Filters bar for mobile */}
            <div className="hidden lg:block">
              {hasActiveFilters && (
                <div className="mb-6 p-4 bg-white rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">
                      Active Filters
                    </h3>
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {filters.categories.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {filters.categories.length} Categories
                        <button
                          onClick={() => handleFilterChange("categories", [])}
                          className="ml-1 hover:text-green-900"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.tags.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {filters.tags.length} Tags
                        <button
                          onClick={() => handleFilterChange("tags", [])}
                          className="ml-1 hover:text-purple-900"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {/* Add more filter chips as needed */}
                  </div>
                </div>
              )}
            </div>
            {/* Products */}
            {loading ? (
              <div className="grid gap-6 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, index) => (
                  <SkeletonProductCard key={index} />
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div
                  className={`grid gap-6 ${
                    mobileGridColumns === 1 ? "grid-cols-1" : "grid-cols-2"
                  } sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}
                >
                  {sortedProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                type="no-search-results"
                title="No products found"
                description={`No products match your search for "${searchQuery}". Try different keywords or browse our categories.`}
                actionText="Browse All Products"
                onAction={() => navigate("/shop")}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Button */}
      <div className="lg:hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="fixed bottom-6 right-6 z-40 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        >
          <Filter className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowFilters(false)}
          />
          <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto h-full">
              <FilterSidebar
                filters={filters}
                availableFilters={availableFilters}
                onFilterChange={handleFilterChange}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                sortBy={sortBy}
                onSortChange={handleSortChange}
              />
            </div>
          </div>
        </div>
      )}

      <div className="lg:hidden">
        <FloatingFilterButton
          filters={filters}
          availableFilters={availableFilters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          hasActiveFilters={hasActiveFilters}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          buttonClassName="fixed bottom-24 right-6 z-40 bg-black text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-gray-400"
          iconClassName="h-6 w-6 text-black"
        />
      </div>
    </div>
  );
});

SearchResultsPage.displayName = "SearchResultsPage";

export default SearchResultsPage;
